import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fanzaApi } from '../../api/fanza.js';
import { lotteryApi } from '../../api/lottery.js';
import { ApiError } from '../../api/client.js';
import type { Category, FzProduct, FzProductInput } from '../../api/types.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SortableTh, SearchInput } from '../../components/ui.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { text, money, num, dateOnly } from '../../lib/format.js';
import {
  checkImageUrl,
  checkLen,
  checkRequired,
  checkDateRange,
  hasFieldErrors,
  inputCls,
  type FieldErrors,
} from '../../lib/formValidation.js';

/**
 * 원본: fanza/product/product_list.php  (상단 메뉴의 "오나홀 관리")
 * 카테고리는 lottery_category 를 공유하므로 lottery API 를 그대로 쓴다.
 *
 * 행을 누르면 그 상품의 경품 관리로 간다 (lottery 랜덤박스 관리와 같다 -
 * 예전의 '경품 관리' 버튼은 없앴다). 관리 칸의 수정·복사는 원본의
 * product_modify.php · product_copy.php 를 모달로 옮긴 것이다.
 */
export function FzProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');
  const sort = params.get('sort') ?? 'randomboxId';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';
  const categoryCode = params.get('categoryCode') ?? '';
  const status = params.get('status') ?? '0';

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debounced = params.get('q') ?? '';

  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy'; product: FzProduct } | null>(null);

  // 카테고리는 lottery_category 공유 (fanza 전용 카테고리 테이블은 없다).
  const categories = useQuery({
    queryKey: ['lottery', 'categories', 'all'],
    queryFn: () => lotteryApi.categories({ page: 1, perPage: 200 }),
    staleTime: 10 * 60 * 1000,
  });

  const query = useQuery({
    queryKey: ['fanza', 'products', { page, sort, dir, q: debounced, categoryCode, status }],
    queryFn: () =>
      fanzaApi.products({
        page,
        perPage: 30,
        sort,
        dir,
        q: debounced || undefined,
        categoryCode: categoryCode || undefined,
        status,
      }),
    placeholderData: keepPreviousData,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['fanza', 'products'] });
  }

  return (
    <section>
      <header className="page-head">
        <h1 className="page-title">오나홀 관리</h1>
        {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
      </header>

      <div className="filters">
        <Field label="검색 (상품명 · 일본어명)">
          <SearchInput
            value={search}
            placeholder="상품명"
            onChange={setSearch}
          />
        </Field>
        <Field label="카테고리">
          <select className="input" value={categoryCode} onChange={(e) => update({ categoryCode: e.target.value })}>
            <option value="">전체</option>
            {categories.data?.items.map((c) => (
              <option key={c.companyCode ?? c.seq} value={c.companyCode ?? ''}>
                {text(c.companyName)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="노출 상태">
          <select className="input" value={status} onChange={(e) => update({ status: e.target.value })}>
            <option value="0">노출 중</option>
            <option value="1">종료 · 숨김</option>
          </select>
        </Field>
      </div>

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorBox error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableTh label="ID" sortKey="randomboxId" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <th>상품</th>
                  <th>카테고리</th>
                  <SortableTh label="가격" sortKey="price" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <SortableTh label="판매 시작" sortKey="saleStart" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="판매 종료" sortKey="saleEnd" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>공개 페이지</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((p, i) => (
                  <tr
                    key={p.randomboxId ?? i}
                    className={p.randomboxId ? 'table__row--clickable' : undefined}
                    onClick={
                      p.randomboxId ? () => navigate(`/fanza/products/${p.randomboxId}/prizes`) : undefined
                    }
                  >
                    <td className="td-right td-muted">{num(p.randomboxId)}</td>
                    <td>
                      <span className="product-cell">
                        {p.thumbnail && <img className="thumb" src={p.thumbnail} alt="" loading="lazy" />}
                        <span>
                          {text(p.name)}
                          {p.jpName && <span className="td-sub">{p.jpName}</span>}
                        </span>
                      </span>
                    </td>
                    <td>{text(p.categoryName)}</td>
                    <td className="td-right">{money(p.price)}</td>
                    <td className="td-muted">{dateOnly(p.saleStart)}</td>
                    <td className="td-muted">{dateOnly(p.saleEnd)}</td>
                    {/* 행 클릭은 경품 관리로 이동하므로, 새 창으로 여는 공개 페이지와
                        관리 칸에서는 이동을 막는다. */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {p.publicUrl ? (
                        <a className="link" href={p.publicUrl} target="_blank" rel="noreferrer">
                          {p.itemType === '2' ? 'kuji_hole' : 'kuji_detail'} ↗
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {p.randomboxId ? (
                        <div className="toolbar toolbar--tight">
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setFormModal({ mode: 'edit', product: p })}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setFormModal({ mode: 'copy', product: p })}
                          >
                            복사
                          </button>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
        </>
      )}

      {formModal && (
        <FzProductFormModal
          mode={formModal.mode}
          product={formModal.product}
          categories={categories.data?.items ?? []}
          onClose={() => setFormModal(null)}
          onRefresh={refresh}
        />
      )}
    </section>
  );
}

/**
 * 폼이 들고 있는 값. FzProductInput(= server fanza/index.ts 의 FANZA_PRODUCT_FIELDS)
 * 과 1:1 이고, 판매 시작 시각만 따로 둔다. PATCH 가 필드 전체를 UPDATE 하므로
 * 여기서 빠진 칸은 저장할 때마다 빈 값으로 덮인다.
 */
interface FzProductFormState {
  categoryCode: string;
  name: string;
  jpName: string;
  saleStart: string;
  saleStartHour: string;
  saleEnd: string;
  deliveryDate: string;
  thumbnail: string;
  movie: string;
  image1: string;
  image2: string;
  price: string;
  priorityRank: string;
  orderColor: string;
  memo: string;
  aPrizeProduct: string;
  bPrizeProduct: string;
  cPrizeProduct: string;
  dPrizeProduct: string;
  ePrizeProduct: string;
}

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));

/**
 * 판매 시작 시각(시). lottery 는 전부 18시라 고정했지만 fanza 는 실데이터에
 * 18시·10시·0시가 섞여 있어, 원본처럼 시를 골라 저장한다(분·초는 원본도 00:00).
 * 값이 없으면 원본 기본값 18시.
 */
function hourOf(dateTime: string | null): string {
  const m = /^\d{4}-\d{2}-\d{2}[ T](\d{2})/.exec(dateTime ?? '');
  return m ? m[1]! : '18';
}

const dateOrEmpty = (v: string | null) => (dateOnly(v) === '-' ? '' : dateOnly(v));
const numToStr = (v: number | null | undefined, fallback = ''): string =>
  v !== null && v !== undefined ? String(v) : fallback;

function toFormState(p: FzProduct): FzProductFormState {
  return {
    categoryCode: p.categoryCode ?? '',
    name: p.name ?? '',
    jpName: p.jpName ?? '',
    saleStart: dateOrEmpty(p.saleStart),
    saleStartHour: hourOf(p.saleStart),
    saleEnd: dateOrEmpty(p.saleEnd),
    deliveryDate: p.deliveryDate ?? '',
    thumbnail: p.thumbnail ?? '',
    movie: p.movie ?? '',
    image1: p.image1 ?? '',
    image2: p.image2 ?? '',
    price: numToStr(p.price),
    priorityRank: numToStr(p.priorityRank, '0'),
    orderColor: p.orderColor || '#ffffff',
    memo: p.memo ?? '',
    aPrizeProduct: p.aPrizeProduct ?? '',
    bPrizeProduct: p.bPrizeProduct ?? '',
    cPrizeProduct: p.cPrizeProduct ?? '',
    dPrizeProduct: p.dPrizeProduct ?? '',
    ePrizeProduct: p.ePrizeProduct ?? '',
  };
}

function toInput(form: FzProductFormState): FzProductInput {
  return {
    categoryCode: form.categoryCode,
    name: form.name,
    jpName: form.jpName,
    saleStart: `${form.saleStart} ${form.saleStartHour}:00:00`,
    // 종료일은 원본처럼 그날 끝까지 판다.
    saleEnd: `${form.saleEnd} 23:59:59`,
    deliveryDate: form.deliveryDate,
    thumbnail: form.thumbnail,
    movie: form.movie,
    image1: form.image1,
    image2: form.image2,
    price: Number(form.price) || 0,
    priorityRank: form.priorityRank === '' ? 0 : Number(form.priorityRank),
    orderColor: form.orderColor,
    memo: form.memo,
    aPrizeProduct: form.aPrizeProduct,
    bPrizeProduct: form.bPrizeProduct,
    cPrizeProduct: form.cPrizeProduct,
    dPrizeProduct: form.dPrizeProduct,
    ePrizeProduct: form.ePrizeProduct,
  };
}

/**
 * GET 응답에 꼭 있어야 하는 키. PATCH 는 필드 전체를 덮어쓰는데, 서버가 아직 옛
 * 버전이면 이 값들이 응답에 없어 폼이 빈 칸으로 채워지고 저장하는 순간 DB 값이
 * 지워진다. 웹만 먼저 배포되고 서버 재시작이 늦어지는 사이를 위한 안전장치다.
 */
const REQUIRED_KEYS = [
  'aPrizeProduct',
  'bPrizeProduct',
  'cPrizeProduct',
  'dPrizeProduct',
  'ePrizeProduct',
] as const;

/**
 * 상품 수정/복사 모달. 원본 fanza/product/product_modify.php · product_copy.php 를
 * 합쳤다 (lottery 와 달리 폭탄구매·할인율이 없고, 판매 시작 시각을 고른다).
 * 원본 수정 폼에 없던 일본어명도 둔다 - 실데이터에 값이 있어 빼면 복사본에서
 * 사라진다. 원본 복사 폼의 폭탄구매는 오나홀은 쓰지 않기로 해서 뺐다(2026-11-03).
 * 레이아웃은 lottery 수정 모달처럼 .form-stack + .row-2/.row-3 로 쌓는다
 * (.form-grid 에 row 가 섞이면 칸이 겹친다 - styles.css 참고).
 */
function FzProductFormModal({
  mode,
  product,
  categories,
  onClose,
  onRefresh,
}: {
  mode: 'edit' | 'copy';
  product: FzProduct;
  categories: Category[];
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [form, setForm] = useState<FzProductFormState>(() => toFormState(product));
  const randomboxId = Number(product.randomboxId);

  // 원본은 item_type=2(오나홀) 카테고리만 보여준다. 지금 상품의 카테고리가 그
  // 밖이면(옛 데이터) 목록에 같이 남겨, 저장할 때 조용히 바뀌지 않게 한다.
  const categoryOptions = categories.filter(
    (c) => c.itemType === '2' || (c.companyCode !== null && c.companyCode === product.categoryCode),
  );

  const serverOutdated = REQUIRED_KEYS.some((k) => !(k in product));

  const [submitted, setSubmitted] = useState(false);
  /** 길이 제한은 fanza_product 컬럼 크기다(name·jp_name 100 · A~E 50 · delivery_date 50 · order_color 20). */
  const errors: FieldErrors = {
    categoryCode: form.categoryCode ? undefined : '카테고리를 선택해주세요.',
    name: checkRequired(form.name, '랜덤박스명', 100),
    jpName: checkLen(form.jpName, 100, '일본어 상품명'),
    thumbnail: checkImageUrl(form.thumbnail),
    movie: checkImageUrl(form.movie),
    image1: checkImageUrl(form.image1),
    image2: checkImageUrl(form.image2),
    deliveryDate: checkLen(form.deliveryDate, 50, '발송 예정'),
    orderColor: checkLen(form.orderColor, 20, '색상'),
    memo: checkLen(form.memo, 5000, '관리용 메모'),
    saleEnd: checkDateRange(form.saleStart, form.saleEnd),
    aPrizeProduct: checkLen(form.aPrizeProduct, 50, 'A 경품 상품명'),
    bPrizeProduct: checkLen(form.bPrizeProduct, 50, 'B 경품 상품명'),
    cPrizeProduct: checkLen(form.cPrizeProduct, 50, 'C 경품 상품명'),
    dPrizeProduct: checkLen(form.dPrizeProduct, 50, 'D 경품 상품명'),
    ePrizeProduct: checkLen(form.ePrizeProduct, 50, 'E 경품 상품명'),
  };
  const shown: FieldErrors = submitted ? errors : {};

  // 복사는 상품을 먼저 만들고 경품을 한 줄씩 복제한다(서버 product-crud.ts 참고 -
  // 원본처럼 한 트랜잭션이 아니다). 경품 일부가 실패하면 새 상품은 남으므로,
  // 모달을 닫지 않고 몇 개가 복사됐는지 알린다.
  const [copyWarning, setCopyWarning] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = toInput(form);
      if (mode === 'edit') {
        await fanzaApi.updateProduct(randomboxId, body);
        return null;
      }
      return fanzaApi.copyProduct(randomboxId, body);
    },
    onSuccess: (result) => {
      onRefresh();
      if (result?.childCopyFailed) {
        setCopyWarning(
          `새 상품(#${result.id})은 만들었지만 경품 일부를 복사하지 못했습니다 (복사된 경품 ${result.copiedChildren}개). 새 상품의 경품 관리에서 빠진 경품을 확인해주세요.`,
        );
        return;
      }
      onClose();
    },
  });

  const valid = form.categoryCode && form.name && form.saleStart && form.saleEnd && form.price !== '';

  const setField = (key: keyof FzProductFormState) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{mode === 'edit' ? '오나홀 상품 수정' : '오나홀 상품 복사'}</h2>

        {mode === 'copy' && (
          <p className="hint">
            <b>{text(product.name)}</b>(#{product.randomboxId})의 내용을 그대로 새 상품으로 만들고,{' '}
            <b>경품도 함께 복사합니다</b> (품절 표시는 풀린 채로). 필요하면 아래 값을 고친 뒤
            저장하세요.
          </p>
        )}

        <form
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (hasFieldErrors(errors)) return;
            save.mutate();
          }}
        >
          <Field label="카테고리" error={shown.categoryCode}>
            <select
              className={inputCls(shown.categoryCode)}
              value={form.categoryCode}
              onChange={(e) => setField('categoryCode')(e.target.value)}
            >
              <option value="">카테고리 선택</option>
              {categoryOptions.map((c) => (
                <option key={c.companyCode ?? c.seq} value={c.companyCode ?? ''}>
                  {c.companyName} ({c.companyCode})
                </option>
              ))}
            </select>
          </Field>

          <div className="row-2">
            <Field label="랜덤박스명" error={shown.name}>
              <input
                className={inputCls(shown.name)}
                value={form.name}
                maxLength={100}
                onChange={(e) => setField('name')(e.target.value)}
              />
            </Field>
            <Field label="일본어 상품명" error={shown.jpName}>
              <input
                className={inputCls(shown.jpName)}
                value={form.jpName}
                maxLength={100}
                onChange={(e) => setField('jpName')(e.target.value)}
              />
            </Field>
          </div>

          <div className="row-2 row-2--stack-sm">
            <Field label="판매 시작">
              <div className="date-hour-row">
                <input
                  className="input"
                  type="date"
                  value={form.saleStart}
                  onChange={(e) => setField('saleStart')(e.target.value)}
                />
                <select
                  className="input"
                  aria-label="판매 시작 시각"
                  value={form.saleStartHour}
                  onChange={(e) => setField('saleStartHour')(e.target.value)}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}시
                    </option>
                  ))}
                </select>
              </div>
            </Field>
            <Field label="판매 종료 (그날 23:59 까지)" error={shown.saleEnd}>
              <input
                className={inputCls(shown.saleEnd)}
                type="date"
                value={form.saleEnd}
                onChange={(e) => setField('saleEnd')(e.target.value)}
              />
            </Field>
          </div>

          <Field label="발송 예정 (자유 입력 · 예: 1월 하순 ~ 2월 하순)" error={shown.deliveryDate}>
            <input
              className={inputCls(shown.deliveryDate)}
              value={form.deliveryDate}
              maxLength={50}
              onChange={(e) => setField('deliveryDate')(e.target.value)}
            />
          </Field>

          <div className="row-2">
            <Field label="대표 이미지" error={shown.thumbnail}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.thumbnail)}
                  value={form.thumbnail}
                  maxLength={255}
                  placeholder="https://..."
                  onChange={(e) => setField('thumbnail')(e.target.value)}
                />
                {form.thumbnail && <img className="thumb thumb--lg" src={form.thumbnail} alt="" />}
              </div>
            </Field>
            <Field label="동영상 URL" error={shown.movie}>
              <input
                className={inputCls(shown.movie)}
                value={form.movie}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => setField('movie')(e.target.value)}
              />
            </Field>
          </div>

          <div className="row-2">
            <Field label="소개 이미지 1" error={shown.image1}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.image1)}
                  value={form.image1}
                  maxLength={255}
                  onChange={(e) => setField('image1')(e.target.value)}
                />
                {form.image1 && <img className="thumb thumb--lg" src={form.image1} alt="" />}
              </div>
            </Field>
            <Field label="소개 이미지 2" error={shown.image2}>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.image2)}
                  value={form.image2}
                  maxLength={255}
                  onChange={(e) => setField('image2')(e.target.value)}
                />
                {form.image2 && <img className="thumb thumb--lg" src={form.image2} alt="" />}
              </div>
            </Field>
          </div>

          <div className="row-3 row-3--even">
            <Field label="판매 가격">
              <input
                className="input"
                type="number"
                min={0}
                value={form.price}
                onChange={(e) => setField('price')(e.target.value)}
              />
            </Field>
            {/* priority_rank 는 tinyint - 그 범위를 넘으면 DB 가 거부한다. */}
            <Field label="표시 순위">
              <input
                className="input"
                type="number"
                min={-128}
                max={127}
                value={form.priorityRank}
                onChange={(e) => setField('priorityRank')(e.target.value)}
              />
            </Field>
            {/* 원본의 '테마 색상' - 주문관리 목록에서 이 오나홀 주문의 제목 색이 된다. */}
            <Field label="색상 (주문관리 제목 색)" error={shown.orderColor}>
              <div className="color-input-row">
                <input
                  className="input color-input"
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(form.orderColor) ? form.orderColor : '#ffffff'}
                  onChange={(e) => setField('orderColor')(e.target.value)}
                />
                <input className={inputCls(shown.orderColor)} value={form.orderColor} maxLength={20} readOnly />
              </div>
            </Field>
          </div>

          {([['a', 'b'], ['c', 'd'], ['e']] as const).map((pair) => (
            <div className="row-2" key={pair[0]}>
              {pair.map((g) => {
                const key = `${g}PrizeProduct` as keyof FzProductFormState;
                return (
                  <Field key={g} label={`${g.toUpperCase()} 경품 상품명`} error={shown[key]}>
                    <input
                      className={inputCls(shown[key])}
                      value={form[key]}
                      maxLength={50}
                      onChange={(e) => setField(key)(e.target.value)}
                    />
                  </Field>
                );
              })}
            </div>
          ))}

          <Field label="관리용 메모" error={shown.memo}>
            <input
              className={inputCls(shown.memo)}
              value={form.memo}
              maxLength={5000}
              onChange={(e) => setField('memo')(e.target.value)}
            />
          </Field>

          {serverOutdated && (
            <div className="qty-msg qty-msg--error" role="alert">
              서버가 아직 A~E 경품 상품명을 보내지 않아 저장을 막았습니다 (지금 저장하면 그 칸이
              지워집니다). 서버를 재시작한 뒤 다시 열어주세요.
            </div>
          )}

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          {copyWarning ? (
            <>
              <div className="qty-msg qty-msg--error" role="alert">
                {copyWarning}
              </div>
              <button type="button" className="btn btn--ghost btn--block" onClick={onClose}>
                닫기
              </button>
            </>
          ) : (
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={!valid || serverOutdated || save.isPending}
            >
              {save.isPending ? '저장 중…' : mode === 'edit' ? '저장하기' : '복사본 만들기'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
